import React, { useState, useEffect } from 'react';
import { Cloud, CloudOff, RefreshCw, AlertCircle, Check, Unplug } from 'lucide-react';
import { syncStatusService, SyncStatus } from '../services/syncStatusService';

interface SyncStatusIndicatorProps {
  isCloudMode: boolean;
  isDisconnected?: boolean;
  onRetryConnection?: () => void;
}

export const SyncStatusIndicator: React.FC<SyncStatusIndicatorProps> = ({ 
  isCloudMode, 
  isDisconnected = false,
  onRetryConnection 
}) => {
  const [syncState, setSyncState] = useState(syncStatusService.getState());

  useEffect(() => {
    return syncStatusService.subscribe(setSyncState);
  }, []);

  // Show disconnected state when logged in to Google but Drive DB not loaded
  if (isDisconnected) {
    return (
      <button 
        onClick={onRetryConnection}
        className="hidden md:flex items-center gap-1 text-[9px] bg-amber-500/10 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded-full hover:bg-amber-500/20 dark:hover:bg-amber-500/30 transition-colors cursor-pointer"
        title="Google Drive connection failed. Click to retry."
      >
        <Unplug size={9} /> Reconnect
      </button>
    );
  }

  if (!isCloudMode) {
    return null;
  }

  const getStatusInfo = (): { icon: React.ReactNode; label: string; className: string } => {
    switch (syncState.status) {
      case 'synced':
        return {
          icon: <Check size={9} />,
          label: 'Synced',
          className: 'bg-green-500/10 text-green-600 dark:text-green-400'
        };
      case 'syncing':
        return {
          icon: <RefreshCw size={9} className="animate-spin" />,
          label: 'Syncing...',
          className: 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
        };
      case 'offline':
        return {
          icon: <CloudOff size={9} />,
          label: 'Offline',
          className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
        };
      case 'pending':
        return {
          icon: <Cloud size={9} />,
          label: `${syncState.pendingChanges} pending`,
          className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
        };
      case 'error':
        return {
          icon: <AlertCircle size={9} />,
          label: 'Sync Error',
          className: 'bg-red-500/10 text-red-600 dark:text-red-400'
        };
      default:
        return {
          icon: <Cloud size={9} />,
          label: 'Drive',
          className: 'bg-green-500/10 text-green-600 dark:text-green-400'
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <span 
      className={`hidden md:flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full ${statusInfo.className}`}
      title={syncState.lastError || (syncState.lastSyncTime ? `Last synced: ${new Date(syncState.lastSyncTime).toLocaleTimeString()}` : 'Not synced yet')}
    >
      {statusInfo.icon} {statusInfo.label}
    </span>
  );
};
